import { createI18n } from 'vue-i18n'

/**
 * Minimal bundled English messages
 * Full locale files are loaded asynchronously and overwrite these.
 */
const bundledEn = {
  nav: {
    home: 'Home',
    about: 'About Us',
    songs: 'Discography',
    albums: 'Discography',
    appearances: 'Appearances',
    gallery: 'Gallery',
    blog: 'Blog',
    contact: 'Contact',
    admin: 'Admin',
    members: 'Members'
  },
  hero: {
    title: 'Chorostalites Band',
    subtitle: 'Traditional sounds with a modern perspective',
    listen: 'Listen',
    spotify: 'Spotify',
    apple: 'Apple Music'
  },
  sections: {
    about: 'About Us',
    songs: 'Discography',
    albums: 'Discography',
    appearances: 'Appearances',
    gallery: 'Gallery',
    contact: 'Contact'
  },
  gallery: {
    personalMoments: 'Personal Moments',
    videoclips: 'Videoclips',
    interviews: 'Interviews',
    noSections: 'No gallery sections available.',
    noImages: 'No images in this section.',
    noContent: 'No content in this section.',
    categories: 'Categories',
    sections: 'Sections',
    items: 'Items',
    photos: 'Photos',
    videos: 'Videos',
    all: 'All Gallery',
    untitled: 'Untitled',
    categoryKey: 'Category key',
    categoryLabelEl: 'Category label (EL)',
    categoryLabelEn: 'Category label (EN)',
    contentStoryEl: 'Content/Story (EL)',
    contentStoryEn: 'Content/Story (EN)',
    captionEl: 'Caption (EL)',
    captionEn: 'Caption (EN)',
    addVideo: 'Add Video',
    videoUrl: 'Video URL',
    noVideos: 'No videos added yet.',
    basics: 'Basics',
    categorySettings: 'Main Section',
    mainSection: 'Section',
    mainSectionHint: 'Choose one of: Moments, Videoclips, Interviews.',
    story: 'Story',
    slugHint: 'Unique URL id; use lowercase and dashes.',
    categoryKeyHint: 'Used for grouping and menu/gallery tabs.',
    categoryLabelHint: 'Shown in the menu and category tabs.',
    orderHint: 'Lower numbers appear first.',
    descriptionHint: 'Short intro shown under the title.',
    contentStoryHint: 'Optional long text shown above media.'
  },
  common: {
    loading: 'Loading...',
    errorLoading: 'Load error',
    notFound: 'Page not found.',
    save: 'Save',
    saving: 'Saving...',
    saveFailed: 'Save failed',
    saved: 'Saved',
    cancel: 'Cancel',
    refresh: 'Refresh',
    newPage: 'New Page',
    edit: 'Edit',
    logout: 'Logout',
    send: 'Send',
    sending: 'Sending...',
    sent: 'Sent!',
    errorSaving: 'Save failed',
    title: 'Title',
    delete: 'Delete',
    name: 'Name',
    role: 'Role',
    more: 'More',
    back: 'Back',
    uploading: 'Uploading...',
    uploadFailed: 'Upload failed',
    deleteFailed: 'Delete failed',
    captionPlaceholder: 'Caption...',
    urlPlaceholder: 'https://...'
  },
  admin: {
    pages: 'Pages',
    pageEdit: 'Edit Page',
    pageNew: 'New Page',
    loginTitle: 'Admin Login',
    email: 'Email',
    password: 'Password',
    login: 'Login',
    enTranslation: 'English Translation',
    loginFailed: 'Login failed',
    authExpired: 'Session expired. Please log in again.',
    sectionAppearance: 'Appearance',
    sectionContact: 'Contact Info',
    sectionLocation: 'Location',
    sectionSocial: 'Social Media',
    slug: 'Slug',
    updated: 'Updated',
    albums: 'Albums',
    albumEdit: 'Edit Album',
    albumNew: 'New Album',
    order: 'Order',
    year: 'Year',
    songsCount: 'Tracks',
    reorderHint: 'Drag rows to reorder. Order saves automatically.',
    confirmDeleteAlbum: 'Delete album?',
    confirmDeleteMember: 'Delete member?',
    confirmDeletePage: 'Delete page?',
    memberNew: 'New Member',
    reorderMembersHint: 'Drag rows to reorder. Saves automatically.',
    settings: 'Settings',
    gallery: 'Gallery',
    galleryEdit: 'Edit Gallery Section',
    galleryNew: 'New Gallery Section',
    galleryImages: 'Gallery Images',
    images: 'images',
    confirmDeleteSection: 'Delete section and all images?',
    galleryReorderHint:
      'Drag images to reorder. First gallery image is used as fallback if no primary image.',
    confirmDeleteImage: 'Delete image?',
    confirmDeleteVideo: 'Delete video?',
    headerImage: 'Header Image',
    topbarImage: 'Top Bar Image',
    primaryColor: 'Primary Color',
    secondaryColor: 'Secondary Color',
    phone: 'Phone',
    emailPrimary: 'Email 1',
    emailSecondary: 'Email 2',
    facebook: 'Facebook',
    youtube: 'YouTube',
    instagram: 'Instagram',
    soundcloud: 'SoundCloud',
    spotify: 'Spotify',
    apple: 'Apple Music',
    showTopBar: 'Show Top Bar',
    appearances: 'Appearances',
    appearanceNew: 'New Appearance',
    confirmDeletePoster: 'Remove poster?',
    videoUrl: 'Video URL'
  },
  appearances: {
    title: 'Live Appearances',
    upcoming: 'Upcoming',
    past: 'Past',
    noUpcoming: 'No upcoming appearances.',
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    calendar: 'Calendar',
    map: 'Map',
    getDirections: 'Get Directions',
    getDirectionsHint: 'Get directions to location',
    directions: 'Directions',
    video: 'Video',
    watchVideo: 'Watch video'
  },
  contact: {
    formTitle: 'Contact Form',
    name: 'Name',
    message: 'Message',
    email: 'Email',
    cellphone: 'Cellphone',
    mapLabel: 'Map',
    mapLocationFallback: 'Location'
  },
  footer: {
    rights: '© 2026 Chorostalites. All rights reserved.'
  },
  lang: { el: 'GR', en: 'EN' }
}

/**
 * Greek overrides
 */
const bundledEl = {
  ...bundledEn,
  nav: {
    ...bundledEn.nav,
    home: 'Αρχική',
    about: 'Σχετικά με εμάς',
    songs: 'Δισκογραφία',
    albums: 'Δισκογραφία',
    appearances: 'Εμφανίσεις',
    gallery: 'Γκαλερί',
    blog: 'Blog',
    contact: 'Επικοινωνία',
    admin: 'Διαχείριση',
    members: 'Μέλη'
  },
  hero: {
    ...bundledEn.hero,
    subtitle: 'Παραδοσιακοί ήχοι με σύγχρονη ματιά',
    listen: 'Ακούστε'
  },
  gallery: {
    ...bundledEn.gallery,
    personalMoments: 'Προσωπικές Στιγμές',
    videoclips: 'Βιντεοκλίπ',
    interviews: 'Συνεντεύξεις'
  },
  appearances: {
    ...bundledEn.appearances,
    title: 'Ζωντανές Εμφανίσεις',
    upcoming: 'Επερχόμενες',
    past: 'Προηγούμενες',
    noUpcoming: 'Καμία επερχόμενη εμφάνιση.',
    weekdays: ['Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ', 'Κυρ']
  },
  common: {
    ...bundledEn.common,
    loading: 'Φόρτωση...',
    notFound: 'Η σελίδα δεν βρέθηκε.',
    save: 'Αποθήκευση',
    cancel: 'Ακύρωση',
    delete: 'Διαγραφή'
  },
  footer: {
    rights: '© 2026 Chorostalites. Με επιφύλαξη παντός δικαιώματος.'
  },
  lang: { el: 'EL', en: 'EN' }
}

const messages = { el: bundledEl, en: bundledEn }
const loadedLocales = new Set()

const saved =
  typeof localStorage !== 'undefined'
    ? localStorage.getItem('locale') || 'el'
    : 'el'

export const i18n = createI18n({
  legacy: false,
  locale: saved,
  fallbackLocale: 'el',
  messages
})

async function loadLocale(lang) {
  if (loadedLocales.has(lang)) return
  try {
    const res = await fetch(`/api/locales/${lang}`)
    if (!res.ok) throw new Error('load failed')
    const data = await res.json()
    i18n.global.setLocaleMessage(lang, data)
    loadedLocales.add(lang)
  } catch {
    // silently ignore
  }
}

export function setLocale(lang) {
  i18n.global.locale.value = lang
  localStorage.setItem('locale', lang)
  loadLocale(lang)
}

// preload initial + English fallback
loadLocale(saved)
loadLocale('en')
